import { HoverCard, HoverCardTrigger, HoverCardContent } from '@/components/ui/hover-card';
import { getSbtPhotoUrl, getColorValue } from '@/lib/vehiclePhoto';

/**
 * Vehicle cell: 32px thumbnail (SBT photo, colour swatch fallback) with a 480px
 * HoverCard zoom, plus plate/VIN and make/model.
 *
 * Extracted from ContractDepositsRecords so flat RPC rows (vehicle_* columns)
 * can render the same treatment as the nested-select tables — zero pixel change.
 */
interface VehicleThumbProps {
    /** Plate number — falls back to VIN, then "Unregistered". */
    registration: string | null;
    vin: string | null;
    make: string | null;
    model: string | null;
    /** Stored colour, hex or name; drives the swatch when there is no photo. */
    color: string | null;
    listingUrl: string | null;
    /** Vehicle `updated_at` — cache-busts the SBT photo. */
    updatedAt: string | null;
}

export function VehicleThumb({
    registration,
    vin,
    make,
    model,
    color,
    listingUrl,
    updatedAt,
}: VehicleThumbProps) {
    // Flat rows carry no vehicle object to null-check; "no vehicle" is "every field null".
    if (!registration && !vin && !make && !model && !color && !listingUrl) return null;

    const photoUrl = getSbtPhotoUrl(listingUrl, updatedAt);

    return (
        <div className="flex items-center gap-2">
            {photoUrl ? (
                <HoverCard openDelay={200} closeDelay={100}>
                    <HoverCardTrigger asChild>
                        <img
                            src={photoUrl}
                            alt=""
                            className="h-8 w-8 rounded-md border border-gray-200 object-cover shrink-0 cursor-zoom-in"
                            onError={(e) => {
                                e.currentTarget.style.display = 'none';
                                e.currentTarget.nextElementSibling?.classList.remove('hidden');
                            }}
                        />
                    </HoverCardTrigger>
                    <HoverCardContent side="right" align="center" className="w-auto p-1" sideOffset={8}>
                        <img src={photoUrl} alt={`${make} ${model}`} className="w-[480px] rounded-md" />
                    </HoverCardContent>
                </HoverCard>
            ) : null}
            <div
                className={`h-8 w-8 rounded-md border border-gray-200 shrink-0${photoUrl ? ' hidden' : ''}`}
                style={{ backgroundColor: getColorValue(color || '') || '#e5e7eb' }}
            />
            <div>
                <span className="font-medium">{registration || vin || 'Unregistered'}</span>
                <p className="text-xs text-muted-foreground">{make} {model}</p>
            </div>
        </div>
    );
}

export default VehicleThumb;
